import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { Box, Stack } from '@mui/system';
import {
  Select,
  Button,
  MenuItem,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  FormHelperText
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { JobBoardLinkMode, OrganizationSetting } from 'src/services/organisation/organisation.types';
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation
} from 'src/services/organisation/organisation.service';

const isValidCareersUrl = (value: string): boolean => {
  if (!value.trim()) return true; // empty means "use the JobTetris-hosted board"

  try {
    return ['http:', 'https:'].includes(new URL(value.trim()).protocol);
  } catch {
    return false;
  }
};

/**
 * The page on the organization's own site where the job board widget is embedded.
 *
 * Shared job links point here instead of the JobTetris-hosted board once it is set. Editable rather
 * than baked into the embed snippet, because a customer's site URL changes over time.
 */
export default function CareersUrlSetting() {

  const { t } = useTranslate();

  const { organization, account } = useAuthContext();

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(organization.id);

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const [value, setValue] = useState<string>('');
  const [mode, setMode] = useState<JobBoardLinkMode>(JobBoardLinkMode.HOSTED);

  const careersUrlSetting = useMemo(
    () => settingsData?.find(
      (setting) => setting.setting_type === OrganizationSetting.JOB_BOARD_CAREERS_URL
    ),
    [settingsData]
  );

  const linkModeSetting = useMemo(
    () => settingsData?.find(
      (setting) => setting.setting_type === OrganizationSetting.JOB_BOARD_LINK_MODE
    ),
    [settingsData]
  );

  useEffect(() => {
    setValue((careersUrlSetting?.setting_value as string) ?? '');
  }, [careersUrlSetting]);

  useEffect(() => {
    setMode((linkModeSetting?.setting_value as JobBoardLinkMode) ?? JobBoardLinkMode.HOSTED);
  }, [linkModeSetting]);

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  const isValid = isValidCareersUrl(value);

  // A website destination without a URL would silently fall back to the hosted board, so block the
  // save rather than letting an admin think they configured something.
  const needsCareersUrl = mode !== JobBoardLinkMode.HOSTED && !value.trim();

  const onSave = async () => {
    if (!careersUrlSetting || !isValid || needsCareersUrl) return;

    try {
      const settings = [{ id: careersUrlSetting.id, value: value.trim() }];

      if (linkModeSetting) {
        settings.push({ id: linkModeSetting.id, value: mode });
      }

      await updateSettings({
        organizationId: organization.id,
        settings,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {t('admin-organization.job-board.destination')}
      </Typography>

      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="job-board-link-mode-label">
            {t('admin-organization.job-board.destination')}
          </InputLabel>
          <Select
            labelId="job-board-link-mode-label"
            value={mode}
            onChange={(e) => setMode(e.target.value as JobBoardLinkMode)}
            disabled={!canEditSettings}
            label={t('admin-organization.job-board.destination')}
          >
            {Object.values(JobBoardLinkMode).map((option) => (
              <MenuItem key={option} value={option}>
                {t(`admin-organization.job-board.destinations.${option}`)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {t('admin-organization.job-board.destination-helper')}
          </FormHelperText>
        </FormControl>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!canEditSettings}
            error={!isValid || needsCareersUrl}
            label={t('admin-organization.job-board.careers-url')}
            placeholder="https://www.example.com/jobs"
            helperText={
              (!isValid && t('admin-organization.job-board.careers-url-invalid'))
              || (needsCareersUrl && t('admin-organization.job-board.careers-url-required'))
              || t('admin-organization.job-board.careers-url-helper')
            }
          />
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!canEditSettings || !isValid || needsCareersUrl || !careersUrlSetting}
            sx={{ mt: 1 }}
          >
            {t('common.update')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
